import React, { useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import { Loader2, Upload, FileSpreadsheet, Download, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ImportData() {
  const [busy, setBusy] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState("");
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purgeBusy, setPurgeBusy] = useState(false);

  async function purgeEmployees() {
    if (purgeConfirm !== "STERGE") return;
    if (!window.confirm("Ești sigur? Se vor șterge DEFINITIV toți angajații. Adminii și clienții rămân.")) return;
    setPurgeBusy(true);
    try {
      const { data } = await api.post(`/staff/users/purge-employees?confirm=STERGE`);
      toast.success(`${data.deleted} angajați șterși definitiv`);
      setPurgeConfirm("");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setPurgeBusy(false); }
  }

  const [dangerBusy, setDangerBusy] = useState(null);
  async function runPurge(key, endpoint, label, confirmMsg) {
    if (purgeConfirm !== "STERGE") return;
    if (!window.confirm(confirmMsg)) return;
    setDangerBusy(key);
    try {
      const { data } = await api.post(`${endpoint}?confirm=STERGE`);
      const n = data.deleted ?? ((data.requests_deleted || 0) + (data.balances_deleted || 0));
      toast.success(`${label}: ${n} înregistrări șterse`);
      setPurgeConfirm("");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setDangerBusy(null); }
  }

  async function downloadTemplate() {
    setDlBusy(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/staff/import/template`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "sablon-import-brisk.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Șablon descărcat");
    } catch (e) { toast.error(`Descărcare eșuată: ${e.message || e}`); }
    finally { setDlBusy(false); }
  }

  async function upload(file) {
    if (!file) return;
    setFileName(file.name);
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/staff/import/xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success("Import finalizat");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[900px]">
        <div className="mb-8">
          <p className="font-mono text-[11px] uppercase tracking-widest text-brand mb-2">Setări</p>
          <h1 className="font-display font-semibold text-[38px] tracking-[-0.02em] leading-tight text-ink mb-2">Import</h1>
          <p className="text-ink-muted text-sm">Creează conturi de angajați și proiecte dintr-un fișier Excel cu foile <b>Angajati</b> și <b>Proiecte</b>.</p>
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-lg flex items-center justify-center bg-neutral-100 text-ink-muted shrink-0">
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-[15px] font-medium text-ink">1. Descarcă șablonul</div>
              <div className="text-[13px] text-ink-muted mt-0.5">
                Completează foaia <b>Angajati</b> (Nume, Email, Parola, Functie, Telefon, Email manager, salarii, tarif) și foaia <b>Proiecte</b> (Nume, Cod, Client, Locatie, Sector, Status, date, bugete).
              </div>
              <button onClick={downloadTemplate} disabled={dlBusy} data-testid="import-template"
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-neutral-200 hover:border-brand hover:text-brand text-sm transition-colors disabled:opacity-60">
                {dlBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Descarcă șablon Excel
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-lg flex items-center justify-center bg-brand/10 text-brand shrink-0">
              <Upload className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-[15px] font-medium text-ink">2. Încarcă fișierul completat</div>
              <div className="text-[13px] text-ink-muted mt-0.5">
                Conturile existente (după email) și proiectele cu cod existent sunt sărite automat. Angajații primesc permisiunea „pontaj propriu".
              </div>
              <label className="mt-3 inline-flex items-center gap-2 px-4 py-2 btn-brand text-sm cursor-pointer disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {busy ? "Se importă..." : "Alege fișier Excel"}
                <input type="file" className="hidden" accept=".xlsx" data-testid="import-file" disabled={busy}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
              </label>
              {fileName && <div className="mt-2 text-[12px] text-ink-muted">Fișier: {fileName}</div>}
            </div>
          </div>
        </div>

        {result && (
          <div className="bg-white border border-neutral-200 rounded-xl p-6 mt-6" data-testid="import-result">
            <h3 className="font-display font-semibold text-lg text-ink mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Rezultat import
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ResultCard title="Angajați" created={result.employees.created} skipped={result.employees.skipped} errors={result.employees.errors} extra={`Manageri legați: ${result.managers_linked}`} />
              <ResultCard title="Proiecte" created={result.projects.created} skipped={result.projects.skipped} errors={result.projects.errors} />
            </div>
            {result.managers_unresolved?.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700 text-sm font-medium mb-1">
                  <AlertTriangle className="h-4 w-4" /> Manageri nerezolvați
                </div>
                <ul className="text-[12px] text-amber-800 list-disc pl-5">
                  {result.managers_unresolved.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        <div className="bg-white border border-red-200 rounded-xl p-6 mt-10">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-lg flex items-center justify-center bg-red-50 text-red-600 shrink-0">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-[15px] font-medium text-red-700">Zonă periculoasă — Resetare date</div>
              <div className="text-[13px] text-ink-muted mt-0.5">
                Acțiuni <b>ireversibile</b>. Scrie <b>STERGE</b> în casetă pentru a activa butoanele. Conturile <b>admin</b> și <b>client</b> nu sunt afectate de ștergerea angajaților.
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <input value={purgeConfirm} onChange={(e) => setPurgeConfirm(e.target.value)}
                  data-testid="purge-confirm" placeholder='Scrie STERGE pentru a confirma'
                  className="border border-neutral-300 px-3 py-2 text-sm w-64 focus:border-red-400 focus:outline-none" />
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <DangerBtn testid="purge-btn" disabled={purgeBusy || purgeConfirm !== "STERGE"} busy={purgeBusy}
                  onClick={purgeEmployees} label="Șterge toți angajații" />
                <DangerBtn testid="purge-assignments-btn" disabled={dangerBusy || purgeConfirm !== "STERGE"} busy={dangerBusy === "asg"}
                  onClick={() => runPurge("asg", "/staff/data/purge-assignments", "Alocări", "Ștergi TOATE alocările angajați↔proiecte?")}
                  label="Șterge toate alocările" />
                <DangerBtn testid="purge-timesheets-btn" disabled={dangerBusy || purgeConfirm !== "STERGE"} busy={dangerBusy === "ts"}
                  onClick={() => runPurge("ts", "/staff/data/purge-timesheets", "Pontaje", "Ștergi TOATE pontajele?")}
                  label="Șterge toate pontajele" />
                <DangerBtn testid="purge-timeoff-btn" disabled={dangerBusy || purgeConfirm !== "STERGE"} busy={dangerBusy === "to"}
                  onClick={() => runPurge("to", "/staff/data/purge-timeoff", "Concedii", "Ștergi TOT ce ține de concedii (cereri + solduri)?")}
                  label="Șterge toate concediile (cereri + solduri)" />
              </div>
            </div>
          </div>
        </div>

      </div>
    </PortalLayout>
  );
}

function DangerBtn({ testid, disabled, busy, onClick, label }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
      {label}
    </button>
  );
}

function ResultCard({ title, created, skipped, errors, extra }) {
  return (
    <div className="border border-neutral-200 rounded-lg p-4">
      <div className="font-medium text-ink mb-2">{title}</div>
      <div className="flex gap-4 text-sm">
        <div><span className="font-display font-bold text-2xl text-emerald-600">{created}</span> <span className="text-ink-muted">create</span></div>
        <div><span className="font-display font-bold text-2xl text-ink-muted">{skipped}</span> <span className="text-ink-muted">sărite</span></div>
      </div>
      {extra && <div className="text-[12px] text-ink-muted mt-2">{extra}</div>}
      {errors?.length > 0 && (
        <ul className="mt-2 text-[12px] text-red-600 list-disc pl-5">
          {errors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}
    </div>
  );
}
